import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { Copy, Printer, Link2Off, Loader2, BadgeCheck, X } from "lucide-react";

// QR + printable one-pager for the public Crash Ready Verified link.
export default function VerifiedShareModal({ caseId, verification, onClose }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    api.post(`/cases/${caseId}/verified-share`)
      .then(({ data }) => setUrl(`${window.location.origin}/api/public/verified/${data.token}/card`))
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not create share link"); onClose(); });
  }, [caseId, onClose]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    catch { toast.info(url); }
  };

  const revoke = async () => {
    setBusy(true);
    try { await api.post(`/cases/${caseId}/verified-share/revoke`); toast.success("Share link revoked"); onClose(); }
    catch { toast.error("Could not revoke link"); }
    finally { setBusy(false); }
  };

  const printPage = () => {
    const crv = verification?.crash_ready_verified || {};
    const comps = crv.components || [];
    const done = comps.filter((c) => c.done).length;
    const qrData = qrRef.current?.querySelector("canvas")?.toDataURL("image/png") || "";
    const rows = comps.map((c) =>
      `<div style="display:flex;align-items:center;gap:8px;padding:3px 0;font-size:13px;color:${c.done ? "#0A192F" : "#94a3b8"}">
        <span style="font-weight:800;color:${c.done ? "#059669" : "#cbd5e1"}">${c.done ? "✓" : "✗"}</span>${c.label}</div>`).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print the one-pager"); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>Crash Ready Verified — ${verification?.case_number || ""}</title></head>
<body style="font-family:Arial,Helvetica,sans-serif;margin:0;padding:40px;color:#0A192F">
  <div style="background:${crv.achieved ? "#059669" : "#0A192F"};color:#fff;border-radius:16px;padding:24px 28px;display:flex;justify-content:space-between;align-items:center">
    <div>
      <div style="font-size:24px;font-weight:900;letter-spacing:1px">${crv.achieved ? "CRASH READY VERIFIED" : `VERIFICATION IN PROGRESS — ${done}/${comps.length}`}</div>
      <div style="font-size:12px;opacity:.85;margin-top:4px">Independent accident verification checklist · Crash Ready</div>
    </div>
    <div style="font-size:34px">🛡</div>
  </div>
  <table style="width:100%;margin-top:20px;font-size:13px;color:#475569"><tr>
    <td><b style="color:#94a3b8;font-size:11px;display:block">CASE ID</b>${verification?.case_number || "—"}</td>
    <td><b style="color:#94a3b8;font-size:11px;display:block">VERIFICATION TIER</b>${verification?.trust_class || "—"}</td>
    <td><b style="color:#94a3b8;font-size:11px;display:block">PRINTED</b>${new Date().toLocaleDateString()}</td>
  </tr></table>
  <div style="display:flex;gap:32px;margin-top:24px;align-items:flex-start">
    <div style="flex:1;column-count:2;column-gap:24px">${rows}</div>
    <div style="text-align:center;width:170px">
      ${qrData ? `<img src="${qrData}" style="width:150px;height:150px;border:1px solid #e2e8f0;border-radius:12px;padding:8px" />` : ""}
      <div style="font-size:10px;color:#64748b;margin-top:6px">Scan to view the live verification status</div>
    </div>
  </div>
  <div style="margin-top:28px;border-top:1px solid #e2e8f0;padding-top:12px;font-size:10px;color:#94a3b8">
    ${crv.disclaimer || "This status documents completed verification steps. It does not guarantee liability, damages, insurance coverage or legal merit."}
  </div>
  <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="verified-share-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-emerald-600" />
            <h2 className="font-heading font-bold text-navy">Share verified status</h2>
          </div>
          <button onClick={onClose} data-testid="verified-share-close" className="text-slate-400 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {!url ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>
        ) : (
          <>
            <div ref={qrRef} className="mt-5 inline-block rounded-2xl border border-slate-100 p-4" data-testid="verified-share-qr">
              <QRCodeCanvas value={url} size={180} fgColor="#0A192F" />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Scan at intake desks, tow yards or attorney consults.</p>
            <div className="mt-4 flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
              <span className="flex-1 text-[11px] text-slate-500 truncate text-left">{url}</span>
              <button onClick={copy} data-testid="verified-share-copy" className="text-cobalt hover:text-navy"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={printPage} data-testid="verified-share-print"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:opacity-90">
                <Printer className="w-4 h-4" /> Print one-pager
              </button>
              <button onClick={revoke} disabled={busy} data-testid="verified-share-revoke"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
                <Link2Off className="w-4 h-4" /> Revoke link
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
